import {
  Add,
  CheckCircleOutline,
  ExpandMore,
  RadioButtonUnchecked,
  Remove,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';

import {
  getAvailableOneTimeAddons,
  getAvailableRecurringBoltOns,
  isRecurringBoltOnSelectionAllowed,
  normalizeOneTimeAddonSelections,
  normalizeRecurringBoltOnSelections,
} from '../../lib/services/configuredAddons';
import type { MarketplaceService } from '../../types/pricing';
import { findVariant } from '../../utils/variantHelpers';

export type RecurringOptionSelection = { addon_key: string };
export type OneTimeOptionSelection = { addon_key: string; quantity: number };

interface ServiceOptionSectionsProps {
  service: MarketplaceService;
  selectedVariant?: string;
  recurringSelections: RecurringOptionSelection[];
  onRecurringSelectionsChange: React.Dispatch<React.SetStateAction<RecurringOptionSelection[]>>;
  oneTimeSelections: OneTimeOptionSelection[];
  onOneTimeSelectionsChange: React.Dispatch<React.SetStateAction<OneTimeOptionSelection[]>>;
}

const formatMonthly = (cents: number) => `$${(cents / 100).toLocaleString('en-US')}/mo`;
const formatMoney = (cents: number) => `$${(cents / 100).toLocaleString('en-US')}`;

const OptionCard: React.FC<{
  name: string;
  description?: string;
  priceLabel: string;
  selected: boolean;
  disabled?: boolean;
  requirement?: string;
  onClick: () => void;
  action?: React.ReactNode;
}> = ({ name, description, priceLabel, selected, disabled, requirement, onClick, action }) => (
  <Box
    sx={{
      p: 1.5,
      border: '1px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      borderRadius: 2,
      bgcolor: selected ? 'action.selected' : 'background.paper',
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {selected
            ? <CheckCircleOutline color="primary" sx={{ fontSize: 17 }} />
            : <RadioButtonUnchecked sx={{ fontSize: 17, color: 'text.secondary' }} />}
          {name}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {priceLabel}
        </Typography>
        {disabled && requirement && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
            {requirement}
          </Typography>
        )}
      </Box>
      {action || (
        <Button
          size="small"
          variant={selected ? 'contained' : 'outlined'}
          disabled={disabled}
          onClick={onClick}
        >
          {selected ? 'Selected' : 'Select'}
        </Button>
      )}
    </Box>
  </Box>
);

const ServiceOptionSections: React.FC<ServiceOptionSectionsProps> = ({
  service,
  selectedVariant,
  recurringSelections,
  onRecurringSelectionsChange,
  oneTimeSelections,
  onOneTimeSelectionsChange,
}) => {
  const variantKey = selectedVariant || service.default_variant_key;
  const recurringOptions = useMemo(
    () => getAvailableRecurringBoltOns(service, variantKey),
    [service, variantKey],
  );
  const oneTimeOptions = useMemo(
    () => getAvailableOneTimeAddons(service, variantKey),
    [service, variantKey],
  );
  const billingOptions = recurringOptions.filter((option) => option.type === 'billing');
  const serviceOptions = recurringOptions.filter((option) => option.type !== 'billing');
  const selectedVariantDefinition = findVariant(service, variantKey);
  const pricingSchedule = Array.isArray((selectedVariantDefinition as any)?.pricing_schedule)
    ? (selectedVariantDefinition as any).pricing_schedule
    : [];
  const pricingNotes = (service as any)?.metadata?.pricing_notes;
  const installation = (service as any)?.metadata?.installation;
  const hasPricingDetails = pricingSchedule.length > 0 || pricingNotes || installation;
  const hasSections = serviceOptions.length > 0 || billingOptions.length > 0 ||
    oneTimeOptions.length > 0 || hasPricingDetails;

  useEffect(() => {
    onRecurringSelectionsChange((current) => {
      const normalized = normalizeRecurringBoltOnSelections(current);
      const allowedKeys = new Set(recurringOptions.map((option) => option.key));
      const filtered = normalized.filter((selection) => allowedKeys.has(selection.addon_key));
      return filtered.filter((selection) => {
        const definition = recurringOptions.find((option) => option.key === selection.addon_key);
        return Boolean(
          definition &&
          isRecurringBoltOnSelectionAllowed(definition, variantKey, filtered),
        );
      });
    });
  }, [onRecurringSelectionsChange, recurringOptions, variantKey]);

  useEffect(() => {
    onOneTimeSelectionsChange((current) => {
      const allowedKeys = new Set(oneTimeOptions.map((option) => option.key));
      return normalizeOneTimeAddonSelections(current)
        .filter((selection) => allowedKeys.has(selection.addon_key));
    });
  }, [onOneTimeSelectionsChange, oneTimeOptions]);

  if (!hasSections) return null;

  const toggleRecurring = (addonKey: string) => {
    const option = recurringOptions.find((entry) => entry.key === addonKey);
    if (!option) return;

    onRecurringSelectionsChange((current) => {
      const normalized = normalizeRecurringBoltOnSelections(current);
      const selected = normalized.some((entry) => entry.addon_key === addonKey);
      if (selected) {
        const next = normalized.filter((entry) => entry.addon_key !== addonKey);
        return next.filter((entry) => {
          const definition = recurringOptions.find((candidate) => candidate.key === entry.addon_key);
          return Boolean(
            definition &&
            isRecurringBoltOnSelectionAllowed(definition, variantKey, next),
          );
        });
      }

      if (!isRecurringBoltOnSelectionAllowed(option, variantKey, normalized)) return normalized;
      const withoutGroup = option.group
        ? normalized.filter((entry) => {
            const selectedDefinition = recurringOptions.find(
              (candidate) => candidate.key === entry.addon_key,
            );
            return selectedDefinition?.group !== option.group;
          })
        : normalized;
      return [...withoutGroup, { addon_key: addonKey }];
    });
  };

  const toggleOneTime = (addonKey: string) => {
    onOneTimeSelectionsChange((current) => {
      const normalized = normalizeOneTimeAddonSelections(current);
      const selected = normalized.some((entry) => entry.addon_key === addonKey);
      if (selected) return normalized.filter((entry) => entry.addon_key !== addonKey);
      return [...normalized, { addon_key: addonKey, quantity: 1 }];
    });
  };

  const setOneTimeQuantity = (addonKey: string, quantity: number, maxQuantity: number) => {
    const nextQuantity = Math.max(0, Math.min(Math.floor(quantity || 0), maxQuantity));
    onOneTimeSelectionsChange((current) => {
      const next = normalizeOneTimeAddonSelections(current)
        .filter((entry) => entry.addon_key !== addonKey);
      if (nextQuantity > 0) next.push({ addon_key: addonKey, quantity: nextQuantity });
      return next;
    });
  };

  const renderRecurringOptions = (options: typeof recurringOptions) => (
    <Stack spacing={1.25}>
      {options.map((option) => {
        const selected = recurringSelections.some((entry) => entry.addon_key === option.key);
        const canSelect = selected ||
          isRecurringBoltOnSelectionAllowed(option, variantKey, recurringSelections);
        return (
          <OptionCard
            key={option.key}
            name={option.name}
            description={option.description}
            priceLabel={`${formatMonthly(option.price_cents)} monthly option`}
            selected={selected}
            disabled={!canSelect}
            requirement={option.requirement_description}
            onClick={() => toggleRecurring(option.key)}
          />
        );
      })}
    </Stack>
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.25 }}>
        Optional details
      </Typography>

      {serviceOptions.length > 0 && (
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>Customize your WiFi</Typography>
              <Typography variant="body2" color="text.secondary">
                Add WiFi coverage or internet backup only if your space needs it.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {renderRecurringOptions(serviceOptions)}
          </AccordionDetails>
        </Accordion>
      )}

      {(billingOptions.length > 0 || oneTimeOptions.length > 0) && (
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>Billing preferences</Typography>
              <Typography variant="body2" color="text.secondary">
                Paperless billing and AutoPay are assumed in the displayed price.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.25}>
              {billingOptions.length > 0 && renderRecurringOptions(billingOptions)}
              {oneTimeOptions.map((option) => {
                const quantity = oneTimeSelections.find(
                  (entry) => entry.addon_key === option.key,
                )?.quantity || 0;
                const maxQuantity = option.max_per_order || 1;
                const selected = quantity > 0;
                return (
                  <OptionCard
                    key={option.key}
                    name={option.name}
                    description={option.description}
                    priceLabel={`${formatMoney(option.price_cents)} one-time, billed by provider`}
                    selected={selected}
                    onClick={() => toggleOneTime(option.key)}
                    action={maxQuantity > 1 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${option.name}`}
                          disabled={quantity <= 0}
                          onClick={() => setOneTimeQuantity(option.key, quantity - 1, maxQuantity)}
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          type="number"
                          value={quantity}
                          onChange={(event) => setOneTimeQuantity(
                            option.key,
                            Number(event.target.value),
                            maxQuantity,
                          )}
                          inputProps={{ min: 0, max: maxQuantity, step: 1 }}
                          sx={{ width: 72 }}
                        />
                        <IconButton
                          size="small"
                          aria-label={`Add ${option.name}`}
                          disabled={quantity >= maxQuantity}
                          onClick={() => setOneTimeQuantity(option.key, quantity + 1, maxQuantity)}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : undefined}
                  />
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {hasPricingDetails && (
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>Pricing details</Typography>
              <Typography variant="body2" color="text.secondary">
                Future pricing, installation charges, and important billing notes.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              {pricingSchedule.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    Monthly price schedule
                  </Typography>
                  {pricingSchedule.map((period: any) => (
                    <Box
                      key={period.label}
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}
                    >
                      <Typography variant="body2" color="text.secondary">{period.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatMonthly(Number(period.recurring_cents || 0))}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {installation && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Professional installation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatMoney(Number(installation.professional_install_cents || 0))} installation
                    {' + '}
                    {formatMoney(Number(installation.service_activation_cents || 0))} activation
                    {' = '}
                    {formatMoney(Number(installation.total_nrc_cents || 0))} provider-billed setup.
                  </Typography>
                </Box>
              )}

              {pricingNotes && (
                <Typography variant="body2" color="text.secondary">
                  {pricingNotes}
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default React.memo(ServiceOptionSections);
